/**
 * /methodology/tqs — public-facing explanation of the Treatment Quality Score.
 *
 * Linked from every doctor profile's reviews block. The whole point: prove
 * to patients (and search engines) that DocSite ratings are computed, not
 * crowdsourced opinion. Forces transparency on the algorithm.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, Lock, Clock, FileText, ArrowLeft } from 'lucide-react';

const QUESTIONS = [
  { weight: 25, q: 'Did the doctor hear your symptoms and condition fully?',
    why: 'Listening time correlates with diagnostic accuracy. Rushed consults cost more downstream.' },
  { weight: 25, q: 'Did the doctor ask follow-up questions, examine, explain, and address your concerns?',
    why: 'A real consult involves both hands and words. This question separates checkbox medicine from craft.' },
  { weight: 20, q: 'Did the doctor tell you the next steps, the reason, and the likely success rate?',
    why: 'Patients who leave with clear next steps adhere to treatment 3× more. Clarity is care.' },
  { weight: 30, q: 'After about 1 week of treatment, did the plan work fairly correctly as the doctor had explained?',
    why: 'Highest weighting. Other platforms never ask this. We ask, and we count it most — because outcome is what matters.' },
];

export default function TqsMethodologyPage() {
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-slate-900">
      <Helmet>
        <title>Treatment Quality Score · Methodology · DocSite</title>
        <meta name="description" content="DocSite ratings are computed, not crowdsourced. Learn how the Treatment Quality Score (TQS) measures doctor quality from objective post-consultation surveys." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-8 font-medium">
          <ArrowLeft size={14} /> Back to DocSite
        </Link>

        <p className="text-xs uppercase tracking-[0.24em] font-bold text-amber-700 mb-3">DocSite methodology</p>
        <h1 className="font-medium leading-[0.95] tracking-tight text-slate-900 mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)' }}>
          Treatment Quality Score (TQS) — measured, not asked.
        </h1>
        <p className="text-slate-700 leading-[1.6]" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>
          Other platforms ask patients to "give 5 stars to your doctor." DocSite doesn't. We send a short 4-question yes/no survey <strong>seven days after every verified consultation</strong> and compute a 0–100 score from the answers. The doctor cannot influence it. There are no stars to game.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <Pillar icon={<Shield size={18} className="text-amber-700" />} title="Verified only" body="Surveys go to patients with confirmed bookings — no anonymous internet reviews." />
          <Pillar icon={<Lock size={18} className="text-amber-700" />} title="Doctor-blind" body="Doctors cannot remove, edit, or pay to suppress results. Period." />
          <Pillar icon={<Clock size={18} className="text-amber-700" />} title="7-day window" body="We wait a week so patients can judge outcome, not bedside warmth." />
        </div>

        <h2 className="font-medium leading-[1] tracking-tight text-slate-900 mt-14 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.85rem, 3.5vw, 2.75rem)' }}>
          The four questions.
        </h2>
        <p className="text-slate-600 leading-relaxed">Each question is yes/no. Each carries a fixed weight (sums to 100). The same questions for every doctor on every specialty.</p>

        <div className="space-y-4 mt-6">
          {QUESTIONS.map((q, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid={`tqs-method-q-${i+1}`}>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-xs uppercase tracking-widest font-bold text-amber-700">Q{i+1}</span>
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Weight {q.weight}%</span>
              </div>
              <p className="font-semibold text-slate-900 text-lg leading-snug" style={{ fontFamily: 'Inter, sans-serif' }}>
                "{q.q}"
              </p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                <strong>Why this question:</strong> {q.why}
              </p>
            </div>
          ))}
        </div>

        <h2 className="font-medium leading-[1] tracking-tight text-slate-900 mt-14 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.85rem, 3.5vw, 2.75rem)' }}>
          Why same-day matters · Freshness decay.
        </h2>
        <p className="text-slate-700 leading-[1.65]" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>
          Q1, Q2, and Q3 are about <strong>what happened in the consult</strong> — memory fades fast. We send the link 30 minutes after the visit and ask patients to submit the same evening. The longer they wait, the less their answers count in the algorithm.
        </p>

        <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden mt-6">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-bold text-xs uppercase tracking-widest text-slate-500">Submission window</th>
                <th className="px-5 py-3 font-bold text-xs uppercase tracking-widest text-slate-500 text-right">Counted at</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[
                { w: 'Same day · 0–24h after visit', m: '100%', tone: 'text-emerald-700' },
                { w: '24–48 hours late', m: '70%', tone: 'text-amber-700' },
                { w: '48–72 hours late', m: '40%', tone: 'text-amber-700' },
                { w: '72 hours+ late', m: '20%', tone: 'text-rose-700' },
              ].map((r, i) => (
                <tr key={i}>
                  <td className="px-5 py-3 text-slate-700">{r.w}</td>
                  <td className={`px-5 py-3 text-right font-bold tabular-nums ${r.tone}`}>{r.m}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-slate-700 leading-[1.65] mt-6" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>
          Q4 — the outcome question — is the opposite. Treatment needs time to work, so the question opens on day 7 and counts at full weight only when answered between days 6–10. Outside that window, it's worth half.
        </p>

        <div className="rounded-2xl bg-sky-50 border border-sky-200 p-5 mt-6 flex items-start gap-3" data-testid="tqs-method-calendar-cta">
          <FileText size={18} className="text-sky-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-900 leading-snug">Reminders without spam — patient-owned calendar</p>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
              Every booking adds three reminders to the patient's phone calendar (visit · same-day rating · day-7 rating). The OS calendar fires every alert — DocSite never pings, never pushes, never sells your number. Subscribe once, the calendar updates itself.
            </p>
          </div>
        </div>

        <h2 className="font-medium leading-[1] tracking-tight text-slate-900 mt-14 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.85rem, 3.5vw, 2.75rem)' }}>
          What you'll see on your (doctor's) profile.
        </h2>
        <ul className="space-y-3 mt-4 text-slate-700">
          {[
            'A 0–100 composite Score ring (TQS). 80+ is excellent, 60–79 is solid, below 60 needs attention.',
            'Four metric bars showing the % of patients who answered "yes" to each question.',
            'Verified patient notes — the qualitative comments — with per-survey ✓/✗ dots showing the actual yes/no answers, no stars.',
            `Score appears only after 5 verified surveys. Until then, "Treatment Quality Score is being established" — we don't fake stars to fill space.`,
            'Last-computed timestamp so you can see how fresh the score is.',
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3 leading-relaxed">
              <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" />
              <span>{t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-12 text-center">
          <Link to="/" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800">
            Back to DocSite
          </Link>
        </div>
      </div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
      <div className="mb-2">{icon}</div>
      <p className="font-semibold text-slate-900 text-base" style={{ fontFamily: 'Inter, sans-serif' }}>{title}</p>
      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}
